import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    IconButton,
    CircularProgress,
    Alert,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Autocomplete,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TablePagination,
    List,
    ListItem,
    ListItemText,
    Select,
    MenuItem,
    Menu,
    Avatar,
    Tooltip,
    Drawer,
    ListItemIcon,
    ListItemSecondaryAction
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
    Refresh as RefreshIcon,
    Add as AddIcon,
    Download as DownloadIcon,
    Pause as PauseIcon,
    PlayArrow as PlayArrowIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ShoppingBag as OrderIcon,
    People as PeopleIcon,
    Build as BuildIcon,
    TrendingUp as TrendingUpIcon,
    LocalShipping as DeliveryIcon,
    AttachMoney as RevenueIcon,
    Info as InfoIcon,
    Close as CloseIcon,
    CloudUpload as PushIcon,
    CalendarMonth as CalendarIcon,
    ShoppingCart as CartIcon,
    LocationOn as LocationIcon,
    NavigateNext as NextIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
    getSubscriptionStats,
    listSubscriptions,
    createSubscription,
    updateSubscription,
    previewSubscription,
    downloadSubscriptionPDF,
    deleteSubscription,
    pushToWooCommerce,
    bulkPushToWooCommerce,
    SubscriptionPreviewResponse,
    SubscriptionItemCreate,
    Address,
    getDeliverySlots
} from '../../api/subscriptions';
import { wooCustomerAPI } from '../../api/wooCustomer';
import { productAPI } from '../../api/product';

// --- Sub-Components ---

const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface DayGroup {
    dayOfWeek: number; // 0-6 (Mon-Sun)
    dates: string[];   // ISO dates
    items: SubscriptionItemCreate[];
}

/**
 * Overview Tab: Forecast & Metrics
 */
function OverviewTab({ stats, loading, onRefresh, onPushAll }: {
    stats: SubscriptionDashboardStats | null,
    loading: boolean,
    onRefresh: () => void,
    onPushAll: (date: string) => void
}) {
    if (loading && !stats) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (!stats) return <Alert severity="info">No forecast data available.</Alert>;

    const days = [
        { label: 'Today', data: stats.today, color: '#1a237e' },
        { label: 'Tomorrow', data: stats.tomorrow, color: '#0d47a1' },
        { label: 'Day After', data: stats.day_after, color: '#1565c0' }
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
                {days.map((day) => (
                    <Grid item xs={12} md={4} key={day.label}>
                        <Card elevation={3} sx={{ borderRadius: 2 }}>
                            <Box sx={{ bgcolor: day.color, color: 'white', p: 1.5, textAlign: 'center' }}>
                                <Typography variant="h6">{day.label}</Typography>
                            </Box>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Deliveries</Typography>
                                        <Typography variant="h4">{day.data.delivery_count}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="body2" color="text.secondary">Est. Revenue</Typography>
                                        <Typography variant="h4">₹{day.data.total_revenue.toLocaleString()}</Typography>
                                    </Box>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>Item Summary</Typography>
                                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                    <List dense>
                                        {Object.entries(day.data.item_summary).length > 0 ? (
                                            Object.entries(day.data.item_summary).map(([name, qty]) => (
                                                <ListItem key={name} sx={{ px: 0 }}>
                                                    <ListItemText primary={name} secondary={`Quantity: ${qty}`} />
                                                </ListItem>
                                            ))
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">No items scheduled.</Typography>
                                        )}
                                    </List>
                                </Box>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PushIcon />}
                                    sx={{ mt: 2 }}
                                    onClick={() => onPushAll(day.data.date)}
                                    disabled={loading || day.data.delivery_count === 0}
                                >
                                    Push all to WooCommerce
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

// Placeholder helper for formatting dates
const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
};

/**
 * Main Component
 */
export default function SubscriptionManagement() {
    const [tab, setTab] = useState(0);
    const [stats, setStats] = useState<SubscriptionDashboardStats | null>(null);
    const [subs, setSubs] = useState<Subscription[]>([]);
    const [pushing, setPushing] = useState(false);
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const loadData = async () => {
        setLoading(true);
        try {
            const [statsData, subsData] = await Promise.all([
                getSubscriptionStats(),
                listSubscriptions()
            ]);
            setStats(statsData);
            setSubs(subsData);
        } catch (error) {
            enqueueSnackbar('Failed to load subscription data', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleTabChange = (_: any, newValue: number) => setTab(newValue);

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to permanently delete this subscription?')) return;
        try {
            await deleteSubscription(id);
            enqueueSnackbar('Subscription deleted permanently', { variant: 'success' });
            loadData();
        } catch (error) {
            enqueueSnackbar('Failed to delete subscription', { variant: 'error' });
        }
    };

    const handleBulkPush = async (date: string) => {
        if (!window.confirm(`Push all active subscriptions for ${new Date(date).toLocaleDateString()} to WooCommerce?`)) return;
        setPushing(true);
        try {
            const res = await bulkPushToWooCommerce(date);
            enqueueSnackbar(`Pushed ${res.success} orders. (${res.already_pushed} already exist)`, {
                variant: res.failed > 0 ? 'warning' : 'success'
            });
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Bulk push failed', { variant: 'error' });
        } finally {
            setPushing(false);
        }
    };

    const handleIndividualPush = async (id: number) => {
        const today = new Date().toISOString().split('T')[0];
        if (!window.confirm(`Create a WooCommerce order for this customer for today (${today})?`)) return;
        setPushing(true);
        try {
            await pushToWooCommerce(id, today);
            enqueueSnackbar('Order created in WooCommerce', { variant: 'success' });
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Push failed', { variant: 'error' });
        } finally {
            setPushing(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                        Subscription Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage recurring B2C deliveries and customer schedules
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadData}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            <Paper sx={{ borderRadius: 2 }}>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab icon={<TrendingUpIcon />} label="Overview" iconPosition="start" />
                    <Tab icon={<PeopleIcon />} label="Customers" iconPosition="start" />
                    <Tab icon={<BuildIcon />} label="Manage & Build" iconPosition="start" />
                </Tabs>

                <Box sx={{ pt: 2 }}>
                    {tab === 0 && <OverviewTab stats={stats} loading={loading || pushing} onRefresh={loadData} onPushAll={handleBulkPush} />}
                    {tab === 1 && (
                        <CustomersTab
                            subs={subs}
                            loading={loading || pushing}
                            onRefresh={loadData}
                            onPushOne={handleIndividualPush}
                            onDelete={handleDelete}
                        />
                    )}
                    {tab === 2 && <ManageTab onComplete={() => { setTab(0); loadData(); }} />}
                </Box>
            </Paper>
        </Box>
    );
}

// --- Import/Define other sub-components locally for now to avoid single large file complexity ---
// We will implement CustomersTab and ManageTab in the next steps.

function CustomersTab({ subs, loading, onRefresh, onPushOne, onDelete }: {
    subs: Subscription[],
    loading: boolean,
    onRefresh: () => void,
    onPushOne: (id: number) => void,
    onDelete: (id: number) => void
}) {
    const { enqueueSnackbar } = useSnackbar();

    const handleStatusChange = async (id: number, status: 'active' | 'paused' | 'cancelled') => {
        try {
            await updateSubscription(id, { status });
            enqueueSnackbar(`Subscription ${status} successfully`, { variant: 'success' });
            onRefresh();
        } catch (error) {
            enqueueSnackbar('Failed to update status', { variant: 'error' });
        }
    };

    const handleDownloadPDF = async (sub: Subscription) => {
        try {
            await downloadSubscriptionPDF(sub.id, sub.customer_name);
            enqueueSnackbar('Downloading PDF...', { variant: 'info' });
        } catch (error) {
            enqueueSnackbar('Failed to generate PDF', { variant: 'error' });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                            <TableRow>
                                <TableCell>Customer</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Discount</TableCell>
                                <TableCell>Last Updated</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subs.map((sub) => (
                                <TableRow key={sub.id} hover>
                                    <TableCell>
                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{sub.customer_name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{sub.customer_email}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={sub.status.toUpperCase()}
                                            size="small"
                                            color={sub.status === 'active' ? 'success' : sub.status === 'paused' ? 'warning' : 'error'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {sub.discount_value > 0 ? (
                                            <Typography variant="body2">
                                                {sub.discount_type === 'percentage' ? `${sub.discount_value}%` : `₹${sub.discount_value.toLocaleString()}`}
                                            </Typography>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(sub.created_at)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" title="Download PDF" onClick={() => handleDownloadPDF(sub)}>
                                            <DownloadIcon color="primary" />
                                        </IconButton>
                                        {sub.status === 'active' ? (
                                            <IconButton size="small" title="Pause" onClick={() => handleStatusChange(sub.id, 'paused')}>
                                                <PauseIcon color="warning" />
                                            </IconButton>
                                        ) : sub.status === 'paused' ? (
                                            <IconButton size="small" title="Resume" onClick={() => handleStatusChange(sub.id, 'active')}>
                                                <PlayArrowIcon color="success" />
                                            </IconButton>
                                        ) : null}
                                        {sub.status === 'cancelled' && (
                                            <IconButton size="small" title="Delete Permanently" onClick={() => onDelete(sub.id)}>
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        )}
                                        {sub.status !== 'cancelled' && (
                                            <IconButton size="small" title="Cancel" onClick={() => handleStatusChange(sub.id, 'cancelled')}>
                                                <PauseIcon sx={{ color: 'grey.500', transform: 'rotate(90deg)' }} titleAccess="Mark as Cancelled" />
                                            </IconButton>
                                        )}
                                        {sub.status === 'active' && (
                                            <IconButton size="small" title="Push Today's Order" onClick={() => onPushOne(sub.id)} color="primary">
                                                <PushIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {subs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        No active subscriptions found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

/**
 * Manage & Build Tab: The Scheduler Workspace
 */
function ManageTab({ onComplete }: { onComplete: () => void }) {
    const { enqueueSnackbar } = useSnackbar();
    const [customers, setCustomers] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
    const [discountType, setDiscountType] = useState<'flat' | 'percentage'>('flat');
    const [discountValue, setDiscountValue] = useState(0);
    const [shippingTotal, setShippingTotal] = useState(0);
    const [shippingMethodTitle, setShippingMethodTitle] = useState('Delivery Charges');

    // Day-based grouping state
    const [dayGroups, setDayGroups] = useState<DayGroup[]>([]);
    const [addDayAnchor, setAddDayAnchor] = useState<null | HTMLElement>(null);

    const [preview, setPreview] = useState<SubscriptionPreviewResponse | null>(null);
    const [saving, setSaving] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Address Editing
    const [addressDrawerOpen, setAddressDrawerOpen] = useState(false);
    const [billingAddress, setBillingAddress] = useState<Address>({});
    const [shippingAddress, setShippingAddress] = useState<Address>({});

    // Delivery Slots
    const [availableSlots, setAvailableSlots] = useState<any[]>([]);
    const [selectedSlotId, setSelectedSlotId] = useState<string>('');
    const [selectedSlotDisplay, setSelectedSlotDisplay] = useState<string>('');

    useEffect(() => {
        const fetchMeta = async () => {
            try {
                const [custRes, prodRes] = await Promise.all([
                    wooCustomerAPI.getCustomers({ limit: 1000 }),
                    productAPI.getProducts({ limit: 1000 })
                ]);
                setCustomers(custRes.customers || []);
                setProducts(prodRes.products || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchMeta();
    }, []);

    // Load available slots when pincode or dayGroups change
    useEffect(() => {
        const pincode = shippingAddress.postcode || selectedCustomer?.shipping_postcode;
        if (pincode && pincode.length >= 6) {
            // Find the first date in dayGroups to help the plugin determine availability
            const firstDate = dayGroups.length > 0 && dayGroups[0].dates.length > 0
                ? dayGroups[0].dates[0]
                : undefined;

            getDeliverySlots(pincode, firstDate).then(slots => {
                setAvailableSlots(slots);
            });
        }
    }, [shippingAddress.postcode, selectedCustomer?.customer_id, dayGroups]);

    const updatePreview = async () => {
        // Flatten day groups into items
        const flattenedItems: SubscriptionItemCreate[] = [];
        dayGroups.forEach(group => {
            group.dates.forEach(dateStr => {
                group.items.forEach(item => {
                    flattenedItems.push({
                        ...item,
                        delivery_date: dateStr,
                        day_of_week: undefined
                    });
                });
            });
        });

        if (flattenedItems.length === 0) return setPreview(null);
        setPreviewLoading(true);
        try {
            const res = await previewSubscription({
                items: flattenedItems,
                discount_type: discountType,
                discount_value: discountValue,
                customer_id: selectedCustomer?.customer_id,
                shipping_total: shippingTotal
            });
            setPreview(res);
        } catch (err) {
            console.error(err);
        } finally {
            setPreviewLoading(false);
        }
    };

    useEffect(() => {
        updatePreview();
    }, [dayGroups, discountType, discountValue, shippingTotal, selectedCustomer?.customer_id]);

    const handleAddDayGroup = (dayIdx: number) => {
        if (dayGroups.find(g => g.dayOfWeek === dayIdx)) {
            enqueueSnackbar(`${dayLabels[dayIdx]} is already added`, { variant: 'info' });
            return;
        }
        setDayGroups([...dayGroups, { dayOfWeek: dayIdx, dates: [], items: [] }]);
    };

    const handleRemoveDayGroup = (dayIdx: number) => {
        setDayGroups(dayGroups.filter(g => g.dayOfWeek !== dayIdx));
    };

    const handleAddDateToGroup = (dayIdx: number, date: dayjs.Dayjs | null) => {
        if (!date) return;
        const dateStr = date.format('YYYY-MM-DD');

        // Ensure it's the correct day of week
        // dayjs 0=Sun...6=Sat. Backend/Labels 0=Mon...6=Sun.
        const backendDay = (date.day() + 6) % 7;
        if (backendDay !== dayIdx) {
            enqueueSnackbar(`Please select a ${dayLabels[dayIdx]}`, { variant: 'warning' });
            return;
        }

        setDayGroups(dayGroups.map(g => {
            if (g.dayOfWeek === dayIdx) {
                if (g.dates.includes(dateStr)) return g;
                return { ...g, dates: [...g.dates, dateStr].sort() };
            }
            return g;
        }));
    };

    const handleRemoveDateFromGroup = (dayIdx: number, dateStr: string) => {
        setDayGroups(dayGroups.map(g => {
            if (g.dayOfWeek === dayIdx) {
                return { ...g, dates: g.dates.filter(d => d !== dateStr) };
            }
            return g;
        }));
    };

    const handleAddProductToGroup = (dayIdx: number, product: any) => {
        if (!product) return;
        setDayGroups(dayGroups.map(g => {
            if (g.dayOfWeek === dayIdx) {
                if (g.items.find(it => it.product_id === product.id)) return g;
                const newItem: SubscriptionItemCreate = {
                    product_id: product.id,
                    quantity: 1,
                    unit_price: product.regular_price || 0,
                    day_of_week: dayIdx
                };
                return { ...g, items: [...g.items, newItem] };
            }
            return g;
        }));
    };

    const handleUpdateItemInGroup = (dayIdx: number, productId: number, field: 'quantity' | 'unit_price', value: number) => {
        setDayGroups(dayGroups.map(g => {
            if (g.dayOfWeek === dayIdx) {
                return {
                    ...g,
                    items: g.items.map(it => it.product_id === productId ? { ...it, [field]: value } : it)
                };
            }
            return g;
        }));
    };

    const handleRemoveItemFromGroup = (dayIdx: number, productId: number) => {
        setDayGroups(dayGroups.map(g => {
            if (g.dayOfWeek === dayIdx) {
                return { ...g, items: g.items.filter(it => it.product_id !== productId) };
            }
            return g;
        }));
    };

    const handleSave = async () => {
        if (!selectedCustomer || dayGroups.length === 0) {
            enqueueSnackbar('Please select a customer and add items', { variant: 'warning' });
            return;
        }

        // Flatten items for backend
        const finalItems: SubscriptionItemCreate[] = [];
        dayGroups.forEach(group => {
            // Group must have either dates OR purely the day_of_week
            // If they picked dates, we generate entries for those dates.
            // If no dates, we just save it as a recurring day entry.
            if (group.dates.length > 0) {
                group.dates.forEach(dateStr => {
                    group.items.forEach(it => {
                        finalItems.push({ ...it, delivery_date: dateStr, day_of_week: undefined });
                    });
                });
            } else {
                group.items.forEach(it => {
                    finalItems.push({ ...it, day_of_week: group.dayOfWeek, delivery_date: undefined });
                });
            }
        });

        if (finalItems.length === 0) {
            enqueueSnackbar('No items to save', { variant: 'warning' });
            return;
        }

        setSaving(true);
        try {
            await createSubscription({
                customer_id: selectedCustomer.customer_id,
                discount_type: discountType,
                discount_value: discountValue,
                items: finalItems,
                billing_address: Object.keys(billingAddress).length > 0 ? billingAddress : undefined,
                shipping_address: Object.keys(shippingAddress).length > 0 ? shippingAddress : undefined,
                shipping_method_title: shippingMethodTitle,
                shipping_total: shippingTotal,
                delivery_slot_id: selectedSlotId || undefined,
                delivery_slot_display: selectedSlotDisplay || undefined
            });
            enqueueSnackbar('Subscription created successfully', { variant: 'success' });
            onComplete();
        } catch (err) {
            enqueueSnackbar('Failed to save subscription', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };


    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    {/* Left Side: Builder */}
                    <Grid item xs={12} md={8.5}>
                        {/* 1. Customer Selection */}
                        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e4f0' }} elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#1a237e', mr: 2 }}><PeopleIcon /></Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Select Customer</Typography>
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={selectedCustomer ? 9 : 12}>
                                    <Autocomplete
                                        options={customers}
                                        getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.email || ''})`}
                                        value={selectedCustomer}
                                        onChange={(_, v) => setSelectedCustomer(v)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Search Woocommerce Customers..."
                                                placeholder="Start typing name or email..."
                                            />
                                        )}
                                    />
                                </Grid>
                                {selectedCustomer && (
                                    <Grid item xs={12} md={3}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<LocationIcon />}
                                            onClick={() => setAddressDrawerOpen(true)}
                                            sx={{ height: 56 }}
                                        >
                                            Edit Address
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>

                        {/* 2. Day & Item Selection */}
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ bgcolor: '#00c853', mr: 2, width: 32, height: 32 }}><CalendarIcon fontSize="small" /></Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Schedule & Items</Typography>
                            </Box>

                            <Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={(e) => setAddDayAnchor(e.currentTarget)}
                                    size="small"
                                >
                                    Add Day of Week
                                </Button>
                                <Menu
                                    anchorEl={addDayAnchor}
                                    open={Boolean(addDayAnchor)}
                                    onClose={() => setAddDayAnchor(null)}
                                >
                                    {dayLabels.map((label, idx) => (
                                        <MenuItem key={idx} onClick={() => { handleAddDayGroup(idx); setAddDayAnchor(null); }}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                        </Box>

                        {dayGroups.length === 0 ? (
                            <Paper sx={{ p: 6, textAlign: 'center', bgcolor: '#f5f7fb', border: '2px dashed #d0d7e2', borderRadius: 2 }}>
                                <CalendarIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                <Typography color="text.secondary">No delivery days added yet.</Typography>
                                <Typography variant="caption" color="text.disabled">Click "Add Day of Week" to start building the schedule.</Typography>
                            </Paper>
                        ) : (
                            dayGroups.map((group, gIdx) => (
                                <Paper key={group.dayOfWeek} sx={{ mb: 4, overflow: 'hidden', border: '1px solid #e0e4f0', borderRadius: 2 }} elevation={0}>
                                    {/* Group Header */}
                                    <Box sx={{ bgcolor: '#f8f9fa', px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e4f0' }}>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                                                {dayLabels[group.dayOfWeek]}s
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Common items for all selected {dayLabels[group.dayOfWeek]}s
                                            </Typography>
                                        </Box>
                                        <IconButton size="small" onClick={() => handleRemoveDayGroup(group.dayOfWeek)} color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    {/* Date Selection for this Group */}
                                    <Box sx={{ p: 2, borderBottom: '1px solid #eef0f7', bgcolor: '#fff' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: group.dates.length > 0 ? 1.5 : 0 }}>
                                            <DatePicker
                                                label={`Add ${dayLabels[group.dayOfWeek]} Dates`}
                                                slotProps={{ textField: { size: 'small', sx: { width: 220 } } }}
                                                onChange={(val) => handleAddDateToGroup(group.dayOfWeek, val as any)}
                                                disablePast
                                            />
                                            <Typography variant="caption" color="text.disabled">
                                                Choose specific dates for this day
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {group.dates.map(d => (
                                                <Chip
                                                    key={d}
                                                    label={dayjs(d).format('MMM D, YYYY')}
                                                    size="small"
                                                    onDelete={() => handleRemoveDateFromGroup(group.dayOfWeek, d)}
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                    </Box>

                                    {/* Item Table for this Group */}
                                    <Box sx={{ p: 2, bgcolor: '#fafbfd', display: 'flex', justifyContent: 'flex-end' }}>
                                        <Autocomplete
                                            size="small"
                                            sx={{ width: 350 }}
                                            options={products}
                                            getOptionLabel={(option) => option.variation_id ? `${option.parent_product} - ${option.product_name}` : option.product_name}
                                            onChange={(_, v) => handleAddProductToGroup(group.dayOfWeek, v)}
                                            renderInput={(params) => <TextField {...params} placeholder="Add product to this day..." />}
                                            value={null}
                                            clearOnBlur
                                        />
                                    </Box>

                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#fff' }}>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Item Details</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }} width={120}>Price (₹)</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }} width={100}>Qty</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }} width={120} align="right">Total</TableCell>
                                                    <TableCell width={50}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {group.items.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.disabled' }}>
                                                            No items added for {dayLabels[group.dayOfWeek]}s.
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    group.items.map((item) => {
                                                        const product = products.find(p => p.id === item.product_id);
                                                        const lineTotal = (item.unit_price || 0) * item.quantity;
                                                        return (
                                                            <TableRow key={item.product_id}>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                        {product?.variation_id ? product.parent_product : product?.product_name}
                                                                    </Typography>
                                                                    {product?.variation_id && <Typography variant="caption" color="text.secondary">{product.product_name}</Typography>}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <TextField
                                                                        size="small"
                                                                        type="number"
                                                                        value={item.unit_price}
                                                                        onChange={(e) => handleUpdateItemInGroup(group.dayOfWeek, item.product_id, 'unit_price', parseFloat(e.target.value) || 0)}
                                                                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <TextField
                                                                        size="small"
                                                                        type="number"
                                                                        value={item.quantity}
                                                                        onChange={(e) => handleUpdateItemInGroup(group.dayOfWeek, item.product_id, 'quantity', parseInt(e.target.value) || 1)}
                                                                        sx={{ width: 70 }}
                                                                        inputProps={{ min: 1 }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>₹{lineTotal.toLocaleString()}</Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <IconButton size="small" onClick={() => handleRemoveItemFromGroup(group.dayOfWeek, item.product_id)}>
                                                                        <CloseIcon fontSize="small" color="disabled" />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            ))
                        )}
                    </Grid>

                    {/* Right Side: Summary Card */}
                    <Grid item xs={12} md={3.5}>
                        <Paper
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                border: '1px solid #1a237e',
                                bgcolor: '#fcfdff',
                                position: 'sticky',
                                top: 20
                            }}
                            elevation={3}
                        >
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e', display: 'flex', alignItems: 'center' }}>
                                <CartIcon sx={{ mr: 1 }} /> Subscription Summary
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Overall Discount</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Select
                                        size="small"
                                        value={discountType}
                                        onChange={(e) => setDiscountType(e.target.value as any)}
                                        sx={{ width: 130 }}
                                    >
                                        <MenuItem value="flat">Flat (Rs)</MenuItem>
                                        <MenuItem value="percentage">Percent (%)</MenuItem>
                                    </Select>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={discountValue}
                                        onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Shipping Charge</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Method Title (e.g. Courier)"
                                        value={shippingMethodTitle}
                                        onChange={(e) => setShippingMethodTitle(e.target.value)}
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        placeholder="Cost"
                                        value={shippingTotal}
                                        onChange={(e) => setShippingTotal(parseFloat(e.target.value) || 0)}
                                        sx={{ width: 150 }}
                                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Delivery Slot</Typography>
                                <Select
                                    fullWidth
                                    size="small"
                                    value={selectedSlotId}
                                    onChange={(e) => {
                                        const slotId = e.target.value;
                                        setSelectedSlotId(slotId);
                                        const slot = availableSlots.find(s => (s.slot_id || s.id) === slotId);
                                        if (slot) setSelectedSlotDisplay(slot.slot_name || slot.name || slot.label);
                                    }}
                                    displayEmpty
                                >
                                    <MenuItem value=""><em>No specific slot</em></MenuItem>
                                    {availableSlots.map(slot => (
                                        <MenuItem key={slot.slot_id || slot.id} value={slot.slot_id || slot.id}>
                                            {slot.slot_name || slot.name || slot.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 2, border: '1px solid #e0e4f0', mb: 3, position: 'relative' }}>
                                {previewLoading && (
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary">Weekly Subtotal</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>₹{(preview?.subtotal || 0).toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, color: 'error.main' }}>
                                    <Typography variant="body2">Applied Discount</Typography>
                                    <Typography variant="body2">- ₹{(preview?.discount_amount || 0).toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary">Shipping</Typography>
                                    <Typography variant="body2">₹{shippingTotal.toLocaleString()}</Typography>
                                </Box>
                                <Divider sx={{ my: 1.5 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Weekly Total</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: '900', color: '#1a237e' }}>
                                        ₹{(preview?.total || 0).toLocaleString()}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1, textAlign: 'center', fontStyle: 'italic' }}>
                                    (Tax inclusive value)
                                </Typography>
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                sx={{
                                    py: 2,
                                    fontWeight: 'bold',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(26, 35, 126, 0.2)'
                                }}
                                disabled={saving || !selectedCustomer || !dayGroups.some(g => g.items.length > 0)}
                                onClick={handleSave}
                                color="primary"
                            >
                                {saving ? <CircularProgress size={24} color="inherit" /> : 'Confirm Subscription'}
                            </Button>

                            <Box sx={{ mt: 3, p: 2, bgcolor: '#fffde7', border: '1px solid #fbc02d', borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ color: '#f57f17', display: 'flex', alignItems: 'center' }}>
                                    <InfoIcon sx={{ fontSize: 14, mr: 1 }} />
                                    Draft orders will be staged in Woocommerce 48h before each delivery.
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Address Drawer */}
                <Drawer
                    anchor="right"
                    open={addressDrawerOpen}
                    onClose={() => setAddressDrawerOpen(false)}
                    PaperProps={{ sx: { width: 450, p: 3 } }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Delivery & Billing Details</Typography>
                        <IconButton onClick={() => setAddressDrawerOpen(false)}><CloseIcon /></IconButton>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Shipping Address</Typography>
                            <Button
                                size="small"
                                onClick={() => {
                                    if (selectedCustomer) {
                                        setShippingAddress({
                                            first_name: selectedCustomer.shipping_first_name || selectedCustomer.first_name,
                                            last_name: selectedCustomer.shipping_last_name || selectedCustomer.last_name,
                                            company: selectedCustomer.shipping_company,
                                            address_1: selectedCustomer.shipping_address_1,
                                            address_2: selectedCustomer.shipping_address_2,
                                            city: selectedCustomer.shipping_city,
                                            state: selectedCustomer.shipping_state,
                                            postcode: selectedCustomer.shipping_postcode,
                                            country: selectedCustomer.shipping_country
                                        });
                                    }
                                }}
                            >
                                Reset to Default
                            </Button>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth size="small" label="First Name"
                                    value={shippingAddress.first_name || ''}
                                    onChange={(e) => setShippingAddress({ ...shippingAddress, first_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth size="small" label="Last Name"
                                    value={shippingAddress.last_name || ''}
                                    onChange={(e) => setShippingAddress({ ...shippingAddress, last_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth size="small" label="Address Line 1"
                                    value={shippingAddress.address_1 || ''}
                                    onChange={(e) => setShippingAddress({ ...shippingAddress, address_1: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth size="small" label="Address Line 2"
                                    value={shippingAddress.address_2 || ''}
                                    onChange={(e) => setShippingAddress({ ...shippingAddress, address_2: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth size="small" label="City"
                                    value={shippingAddress.city || ''}
                                    onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth size="small" label="PIN Code"
                                    value={shippingAddress.postcode || ''}
                                    onChange={(e) => setShippingAddress({ ...shippingAddress, postcode: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Billing Address</Typography>
                            <Button
                                size="small"
                                onClick={() => {
                                    if (selectedCustomer) {
                                        setBillingAddress({
                                            first_name: selectedCustomer.billing_first_name || selectedCustomer.first_name,
                                            last_name: selectedCustomer.billing_last_name || selectedCustomer.last_name,
                                            company: selectedCustomer.billing_company,
                                            address_1: selectedCustomer.billing_address_1,
                                            address_2: selectedCustomer.billing_address_2,
                                            city: selectedCustomer.billing_city,
                                            state: selectedCustomer.billing_state,
                                            postcode: selectedCustomer.billing_postcode,
                                            country: selectedCustomer.billing_country,
                                            email: selectedCustomer.billing_email || selectedCustomer.email
                                        });
                                    }
                                }}
                            >
                                Reset to Default
                            </Button>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth size="small" label="First Name"
                                    value={billingAddress.first_name || ''}
                                    onChange={(e) => setBillingAddress({ ...billingAddress, first_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth size="small" label="Last Name"
                                    value={billingAddress.last_name || ''}
                                    onChange={(e) => setBillingAddress({ ...billingAddress, last_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth size="small" label="Email"
                                    value={billingAddress.email || ''}
                                    onChange={(e) => setBillingAddress({ ...billingAddress, email: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            Note: If billing address is left empty, it will default to the customer's Woocommerce profile.
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 'auto', pt: 4 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setAddressDrawerOpen(false)}
                            sx={{ py: 1.5, fontWeight: 'bold' }}
                        >
                            Save & Close
                        </Button>
                    </Box>
                </Drawer>
            </Box>
        </LocalizationProvider>
    );
}

